import { useState, useEffect } from "react";
import { Coins } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { asBalanceResult } from "@/lib/rpcResults";
import { useAuth } from "@/hooks/useAuth";

export function CreditsBalance() {
  const [balance, setBalance] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const fetchBalance = async () => {
      try {
        const { data, error } = await supabase.rpc('check_balance', {
          p_user_id: user.id
        });

        if (error) {
          console.error('Error fetching balance:', error);
          return;
        }

        const result = asBalanceResult(data);
        if (result) {
          setBalance(result.balance);
        }
      } catch (error) {
        console.error('Failed to fetch balance:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalance();

    // Listen for custom credit update events
    const handleCreditUpdate = () => {
      console.log('Credits updated event received, refreshing balance...');
      fetchBalance();
    };

    window.addEventListener('creditsUpdated', handleCreditUpdate);

    // Also subscribe to Supabase real-time changes (if enabled)
    const channel = supabase
      .channel('balance-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'credit_transactions',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log('Supabase real-time event received, refreshing balance...');
          fetchBalance();
        }
      )
      .subscribe();

    return () => {
      window.removeEventListener('creditsUpdated', handleCreditUpdate);
      supabase.removeChannel(channel);
    };
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted animate-pulse">
        <Coins className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">...</span>
      </div>
    );
  }

  if (balance === null) {
    return null;
  }

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
        balance > 0
          ? 'bg-green-100 dark:bg-green-900/20'
          : 'bg-red-100 dark:bg-red-900/20'
      }`}
      title={`You have ${balance} receipt credit${balance !== 1 ? 's' : ''} remaining`}
    >
      <Coins
        className={`h-4 w-4 ${
          balance > 0
            ? 'text-green-700 dark:text-green-400'
            : 'text-red-700 dark:text-red-400'
        }`}
      />
      <span
        className={`text-sm font-semibold ${
          balance > 0
            ? 'text-green-800 dark:text-green-300'
            : 'text-red-800 dark:text-red-300'
        }`}
      >
        {balance} {balance === 1 ? 'credit' : 'credits'}
      </span>
    </div>
  );
}
